import React, { useState, useEffect, useMemo } from 'react';
import { 
  Library, 
  Plus, 
  FolderPlus, 
  Search, 
  X, 
  Loader2, 
  Trash2, 
  Edit3, 
  Paperclip, 
  ChevronRight, 
  Folder, 
  GripVertical,
  Move,
  FilePlus2,
  ArrowUp,
  AlertTriangle,
  ChevronDown,
  ChevronsDownUp,
  ChevronsUpDown,
  RefreshCw
} from 'lucide-react';
import { collection, query, where, getDocs, addDoc, updateDoc, deleteDoc, doc, Timestamp, orderBy, writeBatch } from 'firebase/firestore';
import { db } from '../firebase';
import { UserProfile, Program, HomeworkAttachment } from '../types';
import { useTranslation } from '../lib/translations';
import { getFileIcon } from '../lib/fileUtils';
import { reconnectGoogle, clearGoogleSession } from '../lib/googleCalendar';
import Tooltip from '../components/Tooltip';

// DnD Kit Imports
import {
  DndContext, 
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
  DragStartEvent,
  DragEndEvent,
  useDroppable,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Added casting to any for import.meta to bypass environment variable type checking
const GOOGLE_DEVELOPER_KEY = (import.meta as any).env.VITE_GOOGLE_PICKER_API_KEY;
const GOOGLE_APP_ID = (import.meta as any).env.VITE_GOOGLE_PICKER_APP_ID;

interface ProgramsProps {
  profile: UserProfile | null;
}

const RootDropZone = ({ isActive }: { isActive: boolean }) => {
  const { setNodeRef, isOver } = useDroppable({ id: 'root-drop-zone' });
  const { t } = useTranslation();
  
  return (
    <div 
      ref={setNodeRef}
      className={`
        relative w-full overflow-hidden transition-all duration-200 ease-out
        ${isActive 
          ? 'h-10 mb-3 opacity-100' 
          : 'h-0 mb-0 opacity-0 pointer-events-none'
        }
      `}
    >
      <div className={`
        w-full h-full border-2 border-dashed rounded-lg flex items-center justify-center gap-2 transition-all
        ${isOver 
          ? 'border-purple-600 bg-purple-600 text-white shadow-lg scale-[1.01]' 
          : 'border-slate-200 dark:border-white/5 bg-slate-50 dark:bg-slate-950/50 text-slate-400'
        }
      `}>
        <ArrowUp size={12} className={isOver ? 'animate-bounce' : ''} />
        <span className="text-[9px] font-black uppercase tracking-[0.2em]">
          {t.programs.moveToRoot}
        </span>
      </div>
    </div>
  );
};

const SortableProgramItem: React.FC<{ 
  program: Program, 
  level: number, 
  isExpanded: boolean, 
  onToggle: (id: string) => void,
  onAddSub: (id: string) => void,
  onEdit: (p: Program) => void,
  onDelete: (p: Program) => void,
  onQuickAddFile: (id: string) => void | Promise<void>,
  children?: React.ReactNode,
  isMatchingSearch?: boolean,
  isBeingHovered?: boolean
}> = ({ 
  program, 
  level, 
  isExpanded, 
  onToggle, 
  onAddSub, 
  onEdit, 
  onDelete, 
  onQuickAddFile,
  children,
  isMatchingSearch,
  isBeingHovered
}) => {
  const { t } = useTranslation();
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id: program.id! });

  const style = {
    transform: isDragging ? CSS.Translate.toString(transform) : undefined,
    transition,
    marginLeft: `${level * 24}px`,
    opacity: isDragging ? 0 : 1, 
    zIndex: isDragging ? 100 : 1
  };

  return (
    <div ref={setNodeRef} style={style} className="space-y-2">
      <div 
        className={`group flex items-center gap-3 p-4 bg-white dark:bg-slate-900 border transition-all rounded-xl ${isBeingHovered ? 'border-purple-600 ring-[6px] ring-purple-500/20 bg-purple-50 dark:bg-purple-900/10' : isMatchingSearch ? 'border-purple-500/50 ring-2 ring-purple-500/10' : 'border-slate-200 dark:border-white/5'} ${!isBeingHovered && 'hover:border-purple-500/30 hover:shadow-lg'}`}
      >
        <div {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing p-1 text-slate-300 hover:text-slate-500">
          <GripVertical size={16} />
        </div>

        <button 
          onClick={() => program.id && onToggle(program.id)}
          className={`p-1 rounded-md hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors ${isExpanded ? 'rotate-90' : ''}`}
        >
          <ChevronRight size={16} className="text-slate-400" />
        </button>
        
        <div className={`p-2 rounded-lg transition-colors ${isBeingHovered ? 'bg-purple-600 text-white' : 'bg-purple-50 dark:bg-purple-900/20 text-purple-600'}`}>
          <Folder size={18} />
        </div>

        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-black text-slate-900 dark:text-white truncate">{program.name}</h3>
          {program.description && <p className="text-[10px] font-medium text-slate-500 truncate">{program.description}</p>}
        </div>

        <div className="flex items-center gap-3 text-[10px] font-black text-slate-400 uppercase tracking-widest mr-4">
          <Paperclip size={12} className="text-purple-400" />
          {program.attachments.length}
        </div>

        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <Tooltip content={t.programs.quickAddTooltip}>
             <button onClick={() => onQuickAddFile(program.id!)} className="p-2 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 rounded-lg transition-all"><FilePlus2 size={16} /></button>
          </Tooltip>
          <Tooltip content={t.programs.addSubTooltip}>
             <button onClick={() => onAddSub(program.id!)} className="p-2 text-slate-400 hover:text-purple-600 hover:bg-purple-50 dark:hover:bg-purple-900/20 rounded-lg transition-all"><FolderPlus size={16} /></button>
          </Tooltip>
          <Tooltip content={t.common.edit}>
            <button onClick={() => onEdit(program)} className="p-2 text-slate-400 hover:text-purple-600 hover:bg-purple-50 dark:hover:bg-purple-900/20 rounded-lg transition-all"><Edit3 size={16} /></button>
          </Tooltip>
          <Tooltip content={t.common.delete}>
            <button onClick={() => onDelete(program)} className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-all"><Trash2 size={16} /></button>
          </Tooltip>
        </div>
      </div>
      
      {isExpanded && (
        <div className="animate-in slide-in-from-top-1 duration-200">
          {children}
        </div>
      )}
    </div>
  );
};

const SortableFileItem: React.FC<{ 
  file: HomeworkAttachment, 
  level: number, 
  onRemove: (driveId: string) => void | Promise<void>,
  isMatchingSearch?: boolean
}> = ({ 
  file, 
  level, 
  onRemove,
  isMatchingSearch
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id: `file-${file.driveId}` });

  const style = {
    transform: isDragging ? CSS.Translate.toString(transform) : undefined,
    transition,
    marginLeft: `${(level + 1) * 24}px`,
    opacity: isDragging ? 0 : 1
  };

  return (
    <div 
      ref={setNodeRef} 
      style={style} 
      className={`flex items-center gap-3 p-3 bg-slate-50 dark:bg-slate-950/50 border transition-all ${isMatchingSearch ? 'border-purple-500/50 ring-2 ring-purple-500/10' : 'border-slate-100 dark:border-white/5'} rounded-lg text-xs font-bold text-slate-600 dark:text-slate-400 mb-2 group`}
    >
      <div {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing p-1 text-slate-300 hover:text-slate-500">
        <GripVertical size={14} />
      </div>
      {getFileIcon(file.mimeType)}
      <span className="truncate flex-1">{file.name}</span>
      <button 
        type="button" 
        onClick={() => onRemove(file.driveId)} 
        className="opacity-0 group-hover:opacity-100 p-1.5 text-slate-400 hover:text-red-500 transition-all"
      >
        <Trash2 size={14} />
      </button>
    </div>
  );
};

const Programs: React.FC<ProgramsProps> = ({ profile }) => {
  const { t, language } = useTranslation();
  const [programs, setPrograms] = useState<Program[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingProgram, setEditingProgram] = useState<Program | null>(null);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [parentId, setParentId] = useState<string | null>(null);
  const [attachments, setAttachments] = useState<HomeworkAttachment[]>([]);
  const [submitting, setSubmitting] = useState(false);

  const [programToDelete, setProgramToDelete] = useState<Program | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const [activeId, setActiveId] = useState<string | null>(null);
  const [overId, setOverId] = useState<string | null>(null);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const fetchPrograms = async () => {
    if (!profile) return;
    setLoading(true);
    try {
      const q = query(
        collection(db, 'programs'), 
        where('teacherId', '==', profile.uid),
        orderBy('order', 'asc'),
        orderBy('createdAt', 'desc')
      );
      const snap = await getDocs(q);
      const data = snap.docs.map(d => ({ id: d.id, ...d.data() } as Program));
      setPrograms(data);
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  };

  useEffect(() => { fetchPrograms(); }, [profile]);

  useEffect(() => {
    if (search.trim().length > 0) {
      const toExpand = new Set<string>();
      const searchLower = search.toLowerCase();

      const checkExpand = (p: Program) => {
        const matches = p.name.toLowerCase().includes(searchLower) || 
                       p.description.toLowerCase().includes(searchLower) ||
                       p.attachments.some(a => a.name.toLowerCase().includes(searchLower));
        
        if (matches) {
          let current = p;
          while (current.parentId) {
            toExpand.add(current.parentId);
            const parent = programs.find(pr => pr.id === current.parentId);
            if (!parent) break;
            current = parent;
          }
        }
      };

      programs.forEach(checkExpand);
      setExpandedGroups(prev => new Set([...prev, ...toExpand]));
    }
  }, [search, programs]);

  const isAncestor = (ancestorId: string, descendantId: string): boolean => {
    let current = programs.find(p => p.id === descendantId);
    while (current && current.parentId) {
      if (current.parentId === ancestorId) return true;
      current = programs.find(p => p.id === current.parentId);
    }
    return false;
  };

  const toggleGroup = (id: string) => {
    const newExpanded = new Set(expandedGroups);
    if (newExpanded.has(id)) newExpanded.delete(id);
    else newExpanded.add(id);
    setExpandedGroups(newExpanded);
  };

  const expandAll = () => {
    const allIds = programs.map(p => p.id!).filter(id => !!id);
    setExpandedGroups(new Set(allIds));
  };

  const collapseAll = () => {
    setExpandedGroups(new Set());
  };

  const expandGroup = (id: string) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      next.add(id);
      return next;
    });
  };

  const handleOpenModal = (program?: Program, parent?: string | null) => {
    if (program) {
      setEditingProgram(program);
      setName(program.name);
      setDescription(program.description);
      setParentId(program.parentId);
      setAttachments(program.attachments);
    } else {
      setEditingProgram(null);
      setName('');
      setDescription('');
      setParentId(parent || null);
      setAttachments([]);
    }
    setIsModalOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!profile || !name.trim()) return;
    setSubmitting(true);
    try {
      const data = {
        teacherId: profile.uid,
        name,
        description,
        parentId,
        attachments,
        createdAt: editingProgram ? editingProgram.createdAt : Timestamp.now(),
        order: editingProgram?.order ?? programs.length
      };

      if (editingProgram?.id) {
        await updateDoc(doc(db, 'programs', editingProgram.id), data);
        if (editingProgram.parentId) expandGroup(editingProgram.parentId);
      } else {
        const docRef = await addDoc(collection(db, 'programs'), data);
        expandGroup(docRef.id);
        if (parentId) expandGroup(parentId);
      }
      setIsModalOpen(false);
      fetchPrograms();
    } catch (e) {
      console.error(e);
    }
    setSubmitting(false);
  };

  const confirmDelete = async () => {
    if (!programToDelete || !programToDelete.id) return;
    setIsDeleting(true);
    try {
      const batch = writeBatch(db);
      const children = programs.filter(p => p.parentId === programToDelete.id);
      children.forEach(child => {
        batch.update(doc(db, 'programs', child.id!), { parentId: programToDelete.parentId });
      });
      batch.delete(doc(db, 'programs', programToDelete.id));
      await batch.commit();
      setProgramToDelete(null);
      fetchPrograms();
    } catch (e) {
      console.error(e);
    }
    setIsDeleting(false);
  };

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string);
  };

  const handleDragOver = (event: any) => {
    setOverId(event.over?.id || null);
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveId(null);
    setOverId(null);
    if (!over || !profile) return;

    const activeId = active.id as string;
    const overId = over.id as string;

    if (activeId.startsWith('file-')) {
      const fileDriveId = activeId.replace('file-', '');
      const sourceProgram = programs.find(p => p.attachments.some(a => a.driveId === fileDriveId));
      const targetProgram = programs.find(p => p.id === overId || p.attachments.some(a => `file-${a.driveId}` === overId));

      if (sourceProgram && targetProgram && sourceProgram.id !== targetProgram.id) {
        const fileToMove = sourceProgram.attachments.find(a => a.driveId === fileDriveId)!;
        const newSourceAttachments = sourceProgram.attachments.filter(a => a.driveId !== fileDriveId);
        const newTargetAttachments = [...targetProgram.attachments, fileToMove];

        const batch = writeBatch(db);
        batch.update(doc(db, 'programs', sourceProgram.id!), { attachments: newSourceAttachments });
        batch.update(doc(db, 'programs', targetProgram.id!), { attachments: newTargetAttachments });
        await batch.commit();
        expandGroup(targetProgram.id!);
        fetchPrograms();
      }
    } else {
      const activeProgram = programs.find(p => p.id === activeId);
      
      if (overId === 'root-drop-zone') {
        if (activeProgram?.parentId !== null) {
          await updateDoc(doc(db, 'programs', activeId), { parentId: null });
          fetchPrograms();
        }
        return;
      }

      const overProgram = programs.find(p => p.id === overId || p.attachments.some(a => `file-${a.driveId}` === overId));

      if (activeProgram && overProgram && activeId !== overProgram.id) {
        if (isAncestor(activeId, overProgram.id!)) {
          alert(t.programs.invalidMove);
          return;
        }

        const newParentId = overProgram.id!;
        const oldIndex = programs.findIndex(p => p.id === activeId);
        const newIndex = programs.findIndex(p => p.id === overProgram.id);
        const newPrograms = arrayMove([...programs], oldIndex, newIndex);
        
        const batch = writeBatch(db);
        newPrograms.forEach((p, idx) => {
          const update: any = { order: idx };
          if (p.id === activeId) {
            update.parentId = newParentId;
            expandGroup(newParentId);
          }
          batch.update(doc(db, 'programs', p.id!), update);
        });
        await batch.commit();
        fetchPrograms();
      }
    }
  };

  const openPicker = (forceAccountSelect = false) => {
    return new Promise<HomeworkAttachment[]>((resolve, reject) => {
      const gapi = (window as any).gapi;
      if (!gapi) { reject(new Error('GAPI script not loaded')); return; }
      
      gapi.load('picker', {
        callback: async () => {
          try {
            let token = sessionStorage.getItem('google_access_token');
            if (!token || forceAccountSelect) {
              token = await reconnectGoogle(forceAccountSelect);
            }
            if (!token) { reject(new Error('Auth required')); return; }

            if (!(window as any).google?.picker) {
              reject(new Error('Google Picker namespace not available'));
              return;
            }

            // Basic DocsView without chained methods that might fail if library version varies
            const docsView = new (window as any).google.picker.DocsView((window as any).google.picker.ViewId.DOCS);

            const origin = window.location.protocol + '//' + window.location.host;

            const picker = new (window as any).google.picker.PickerBuilder()
              .addView(docsView)
              .setOAuthToken(token)
              .setDeveloperKey(GOOGLE_DEVELOPER_KEY)
              .setAppId(GOOGLE_APP_ID)
              .setOrigin(origin)
              .setCallback((data: any) => {
                if (data.action === (window as any).google.picker.Action.PICKED) {
                  resolve(data.docs.map((d: any) => ({
                    name: d.name, mimeType: d.mimeType, webViewLink: d.url, driveId: d.id
                  })));
                } else if (data.action === (window as any).google.picker.Action.CANCEL) {
                  resolve([]);
                }
              })
              .build();
            picker.setVisible(true);
          } catch (pickerErr) {
            console.error('Picker internal launch failure', pickerErr);
            clearGoogleSession();
            reject(pickerErr);
          }
        },
        onerror: (err: any) => {
          console.error('GAPI Picker Load Error', err);
          reject(new Error('Failed to load Google Picker library'));
        }
      });
    });
  };

  const handleAttachFiles = async () => {
    try {
      const picked = await openPicker();
      if (picked.length > 0) {
        setAttachments(prev => [...prev, ...picked]);
      }
    } catch (err) { 
      console.error('Picker Flow Error:', err);
      if (window.confirm('Google Drive integration failed. Would you like to select your account again?')) {
        try {
          clearGoogleSession();
          const picked = await openPicker(true);
          if (picked.length > 0) setAttachments(prev => [...prev, ...picked]);
        } catch (retryErr) {
          console.error('Retry failed', retryErr);
        }
      }
    }
  };

  const handleQuickAddFiles = async (programId: string) => {
    try {
      const picked = await openPicker();
      if (picked.length > 0) {
        const prog = programs.find(p => p.id === programId);
        if (prog) {
          const newAttachments = [...prog.attachments, ...picked];
          await updateDoc(doc(db, 'programs', programId), { attachments: newAttachments });
          expandGroup(programId);
          fetchPrograms();
        }
      }
    } catch (err) { 
      console.error('Quick Add Picker Error:', err);
      if (window.confirm('Google Drive integration failed. Would you like to re-select your account and try again?')) {
        try {
          clearGoogleSession();
          const picked = await openPicker(true);
          if (picked.length > 0) {
            const prog = programs.find(p => p.id === programId);
            if (prog) {
              const newAttachments = [...prog.attachments, ...picked];
              await updateDoc(doc(db, 'programs', programId), { attachments: newAttachments });
              expandGroup(programId);
              fetchPrograms();
            }
          }
        } catch (retryErr) {
          console.error('Retry failed', retryErr);
        }
      }
    }
  };

  const removeFileFromProgram = async (programId: string, driveId: string) => {
    const prog = programs.find(p => p.id === programId);
    if (!prog) return;
    const newAtt = prog.attachments.filter(a => a.driveId !== driveId);
    await updateDoc(doc(db, 'programs', programId), { attachments: newAtt });
    fetchPrograms();
  };

  const hierarchy = useMemo(() => {
    const map = new Map<string | null, Program[]>();
    programs.forEach(p => {
      const parent = p.parentId;
      if (!map.has(parent)) map.set(parent, []);
      map.get(parent)!.push(p);
    });
    return map;
  }, [programs]);

  const renderGroupTree = (parentId: string | null = null, level: number = 0) => {
    const items = hierarchy.get(parentId) || [];
    const searchLower = search.toLowerCase();

    const filterRecursive = (p: Program): boolean => {
      if (search.trim() === '') return true;
      const selfMatch = p.name.toLowerCase().includes(searchLower) || 
                         p.description.toLowerCase().includes(searchLower);
      const fileMatch = p.attachments.some(a => a.name.toLowerCase().includes(searchLower));
      const childMatch = (hierarchy.get(p.id!) || []).some(filterRecursive);
      return selfMatch || fileMatch || childMatch;
    };

    const filtered = items.filter(filterRecursive);

    if (filtered.length === 0 && level === 0 && items.length === 0) {
      return (
        <div className="py-20 text-center border-2 border-dashed border-slate-200 dark:border-white/5 rounded-xl">
          <Library size={48} className="text-slate-200 dark:text-slate-800 mx-auto mb-4" />
          <p className="text-slate-400 font-bold italic">{t.programs.noGroups}</p>
        </div>
      );
    }

    return (
      <SortableContext 
        items={filtered.map(p => p.id!)} 
        strategy={verticalListSortingStrategy}
      >
        {filtered.map(p => {
          const isMatchingSearch = search.trim() !== '' && (
            p.name.toLowerCase().includes(searchLower) || 
            p.description.toLowerCase().includes(searchLower)
          );

          const isGroupHovered = (overId === p.id || p.attachments.some(a => `file-${a.driveId}` === overId)) && activeId !== p.id;

          return (
            <SortableProgramItem
              key={p.id}
              program={p}
              level={level}
              isExpanded={expandedGroups.has(p.id!)}
              isMatchingSearch={isMatchingSearch}
              onToggle={toggleGroup}
              onAddSub={(id) => handleOpenModal(undefined, id)}
              onEdit={(prog) => handleOpenModal(prog)}
              onDelete={(prog) => setProgramToDelete(prog)}
              onQuickAddFile={handleQuickAddFiles}
              isBeingHovered={isGroupHovered}
            >
              <div className="space-y-1">
                <SortableContext 
                  items={p.attachments.map(a => `file-${a.driveId}`)} 
                  strategy={verticalListSortingStrategy}
                >
                  {p.attachments
                    .filter(a => search.trim() === '' || a.name.toLowerCase().includes(searchLower))
                    .map(f => (
                      <SortableFileItem 
                        key={f.driveId} 
                        file={f} 
                        level={level} 
                        isMatchingSearch={search.trim() !== '' && f.name.toLowerCase().includes(searchLower)}
                        onRemove={(fid) => removeFileFromProgram(p.id!, fid)} 
                      />
                    ))
                  }
                </SortableContext>
                {renderGroupTree(p.id!, level + 1)}
              </div>
            </SortableProgramItem>
          );
        })}
      </SortableContext>
    );
  };

  return (
    <div className="max-w-4xl mx-auto space-y-10 pb-32">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
        <div>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-lg bg-purple-50 dark:bg-purple-900/10 border border-purple-100 dark:border-purple-500/20 text-purple-600 dark:text-purple-400 text-[10px] font-black uppercase tracking-widest shadow-sm mb-4">
            <Library size={12} className="animate-pulse" /> {t.nav.programs}
          </div>
          <h1 className="text-4xl font-black text-slate-900 dark:text-white tracking-tighter">{t.programs.title}</h1>
          <p className="text-slate-500 font-medium">{t.programs.desc}</p>
        </div>
        <button 
          onClick={() => handleOpenModal()}
          className="flex items-center justify-center gap-2 bg-purple-600 text-white px-8 py-4 rounded-xl font-black text-sm transition-all hover:bg-purple-700 hover:scale-105 active:scale-95 shadow-xl shadow-purple-500/20"
        >
          <Plus size={20} /> {t.programs.newGroup}
        </button>
      </div>

      <div className="flex flex-col md:flex-row gap-4 items-center">
        <div className="relative group flex-1 w-full">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-purple-600 transition-colors" size={20} />
          <input 
            type="text" 
            placeholder={t.programs.searchPlaceholder} 
            value={search} 
            onChange={(e) => setSearch(e.target.value)} 
            className="w-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-white/5 rounded-xl py-3.5 px-12 text-sm font-medium focus:ring-4 focus:ring-purple-500/10 focus:border-purple-500 outline-none shadow-sm transition-all" 
          />
        </div>
        
        <div className="flex items-center gap-2 shrink-0">
          <button 
            onClick={expandAll}
            className="flex items-center gap-2 px-4 py-3 bg-white dark:bg-slate-900 border border-slate-200 dark:border-white/5 text-slate-600 dark:text-slate-300 rounded-xl text-[10px] font-black uppercase tracking-widest hover:border-purple-500/30 transition-all shadow-sm"
          >
            <ChevronsUpDown size={14} /> {t.common.expandAll}
          </button>
          <button 
            onClick={collapseAll}
            className="flex items-center gap-2 px-4 py-3 bg-white dark:bg-slate-900 border border-slate-200 dark:border-white/5 text-slate-600 dark:text-slate-300 rounded-xl text-[10px] font-black uppercase tracking-widest hover:border-purple-500/30 transition-all shadow-sm"
          >
            <ChevronsDownUp size={14} /> {t.common.collapseAll}
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-20">
          <Loader2 className="animate-spin text-purple-600" size={40} />
        </div>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragOver={handleDragOver}
          onDragEnd={handleDragEnd}
        >
          <div className="space-y-4 min-h-[400px]">
            <RootDropZone isActive={!!activeId && !activeId.startsWith('file-')} />
            {renderGroupTree(null)}
          </div>
          <DragOverlay dropAnimation={null}>
            {activeId ? (
              <div className="p-4 bg-purple-600 text-white rounded-xl shadow-2xl flex items-center gap-3 opacity-90 border-2 border-white/20 scale-105">
                <Move size={20} />
                <span className="font-black text-sm uppercase tracking-widest">
                  {activeId.startsWith('file-') ? t.programs.movingFile : t.programs.movingGroup}
                </span>
              </div>
            ) : null}
          </DragOverlay>
        </DndContext>
      )}

      {/* Program Create/Edit Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-6 bg-slate-950/80 backdrop-blur-xl animate-in fade-in duration-300 mt-0 !mt-0">
          <div className="bg-white dark:bg-slate-900 w-full max-w-lg rounded-xl border border-slate-200 dark:border-white/5 p-10 shadow-2xl relative overflow-hidden">
            <button onClick={() => setIsModalOpen(false)} className="absolute top-8 right-8 text-slate-400 hover:text-slate-600 dark:hover:text-white transition-all"><X size={24} /></button>
            <h2 className="text-3xl font-black text-slate-900 dark:text-white tracking-tighter mb-8">{editingProgram ? t.programs.editGroup : t.programs.newGroup}</h2>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2"><label className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1">{t.programs.groupName}</label><input required type="text" value={name} onChange={(e) => setName(e.target.value)} className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-lg py-3 px-4 text-sm font-black focus:ring-4 focus:ring-purple-500/10 outline-none transition-all" /></div>
              <div className="space-y-2"><label className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1">{t.programs.groupDesc}</label><textarea rows={2} value={description} onChange={(e) => setDescription(e.target.value)} className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-lg py-3 px-4 text-sm font-medium focus:ring-4 focus:ring-purple-500/10 outline-none transition-all resize-none" /></div>
              <div className="space-y-4">
                <div className="flex items-center justify-between"><label className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1">{t.programs.assetsTitle}</label><button type="button" onClick={handleAttachFiles} className="flex items-center gap-2 text-[9px] font-black text-purple-600 uppercase tracking-widest hover:underline transition-all"><Paperclip size={14} /> {t.programs.addFile}</button></div>
                <div className="max-h-48 overflow-y-auto space-y-2 custom-scrollbar pr-2">{attachments.map((file, idx) => (<div key={idx} className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-950 border border-slate-100 dark:border-white/5 rounded-lg"><div className="flex items-center gap-3 truncate flex-1 text-xs font-bold">{getFileIcon(file.mimeType)}<span className="truncate">{file.name}</span></div><button type="button" onClick={() => setAttachments(prev => prev.filter((_, i) => i !== idx))} className="text-slate-400 hover:text-red-500 pl-2"><X size={14} /></button></div>))}{attachments.length === 0 && (<p className="text-[10px] text-slate-400 italic text-center py-4">{t.programs.noAssets}</p>)}</div>
              </div>
              <button type="submit" disabled={submitting} className="w-full py-4 bg-purple-600 text-white rounded-lg font-black text-[11px] uppercase tracking-[0.2em] transition-all shadow-xl shadow-purple-500/20 hover:bg-purple-700 active:scale-95 disabled:opacity-50 flex items-center justify-center gap-3">{submitting ? <Loader2 className="animate-spin" size={20} /> : t.common.save}</button>
            </form>
          </div>
        </div>
      )}

      {programToDelete && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center p-6 bg-slate-950/80 backdrop-blur-xl animate-in fade-in duration-300 mt-0 !mt-0">
          <div className="bg-white dark:bg-slate-900 w-full max-w-md rounded-xl p-10 shadow-2xl text-center border border-slate-200 dark:border-white/5">
            <div className="w-16 h-16 bg-red-100 dark:bg-red-900/20 text-red-600 rounded-2xl flex items-center justify-center mx-auto mb-6"><AlertTriangle size={32} /></div>
            <h3 className="text-2xl font-black text-slate-900 dark:text-white mb-3">{t.programs.confirmDeleteTitle}</h3>
            <p className="text-slate-500 text-sm mb-10 leading-relaxed font-medium">{t.programs.confirmDeleteDesc.replace('{name}', programToDelete.name)}</p>
            <div className="flex flex-col gap-3">
              <button disabled={isDeleting} onClick={confirmDelete} className="w-full bg-red-600 text-white py-4 rounded-xl font-black text-[11px] uppercase tracking-widest shadow-xl shadow-red-500/20 hover:bg-red-700 transition-all active:scale-95 flex items-center justify-center gap-2">{isDeleting ? <Loader2 size={18} className="animate-spin" /> : <><Trash2 size={18} /> {t.common.delete}</>}</button>
              <button disabled={isDeleting} onClick={() => setProgramToDelete(null)} className="w-full bg-slate-100 dark:bg-slate-800 text-slate-900 dark:text-white py-4 rounded-xl font-black text-[11px] uppercase tracking-widest transition-all hover:bg-slate-200 active:scale-95">{t.common.cancel}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Programs;